import { ConvertUSTPrice } from './formatting'
import { GetRowIdParams } from 'ag-grid-community'

export const objectsAreEqual = <K extends string = string>(
  object1: Partial<Record<K, unknown>> | undefined,
  object2: Partial<Record<K, unknown>> | undefined
) => {
  if (!object1 || !object2) return !object1 && !object2
  const entries1 = Object.entries(object1)
  const entries2 = Object.entries(object2)
  if (entries1.length !== entries2.length) return false
  for (const [key, value] of entries1) {
    const other = (object2 as Record<string, unknown>)[key]
    if (value instanceof Date || other instanceof Date) {
      if (
        !(value instanceof Date) ||
        !(other instanceof Date) ||
        value.getTime() !== other.getTime()
      ) {
        return false
      }
    } else if (typeof value === 'object' && typeof other === 'object') {
      if (
        !objectsAreEqual(
          value as Record<string, unknown>,
          other as Record<string, unknown>
        )
      ) {
        return false
      }
    } else {
      if (other !== value) return false
    }
  }
  return true
}
export const calculateOrderPriceFromInput = (
  input: string,
  isTreasury: boolean
) => {
  const numberPrice = Number(input)
  if (!isNaN(numberPrice) || !isTreasury) return numberPrice || 0 // NaN is falsey, so this will also cast NaN to 0
  return ConvertUSTPrice(input)
}

export const stringCompare = (a: string, b: string) => {
  return a.localeCompare(b)
}
export const numericCompare = (a: number, b: number) => a - b
export const dateCompare = (a: Date, b: Date) =>
  numericCompare(a.valueOf(), b.valueOf())

export const createObjectCompare =
  <P extends PropertyKey, T, O extends Record<P, T>>(
    prop: P,
    compareFn: (a: T, b: T) => number
  ) =>
  (a: O, b: O) =>
    compareFn(a[prop], b[prop])

type FirstParam<T extends (...args: any[]) => any> = Parameters<T>[0]

export const createSortByFields = <
  A,
  T extends (...args: A[]) => number,
  O extends FirstParam<T>
>(
  compareFns: T[]
) => {
  return (a: O, b: O) => {
    return compareFns.reduce((result, sf) => {
      if (result) return result
      return sf(a, b)
    }, 0)
  }
}

export type Split<S extends string, D extends string> = string extends S
  ? string[]
  : S extends ''
  ? []
  : S extends `${infer T}${D}${infer U}`
  ? [T, ...Split<U, D>]
  : [S]

export type PathSegments<T extends string> = Split<T, '.'>

// Type to access nested object properties safely - handles optional properties
export type NestedKeyOf<ObjectType extends object> = {
  [Key in keyof ObjectType & (string | number)]: ObjectType[Key] extends
    | object
    | undefined
    ? ObjectType[Key] extends undefined
      ? `${Key}`
      : `${Key}` | `${Key}.${NestedKeyOf<NonNullable<ObjectType[Key]>>}`
    : `${Key}`
}[keyof ObjectType & (string | number)]

// Enhanced GetNestedType that handles optional properties
export type GetNestedType<T, K extends string> = K extends keyof T
  ? T[K]
  : K extends `${infer K1}.${infer K2}`
  ? K1 extends keyof T
    ? T[K1] extends object | undefined
      ? T[K1] extends undefined
        ? undefined
        : GetNestedType<NonNullable<T[K1]>, K2> | undefined
      : never
    : never
  : never

// Helper type for arrays of nested keys
export type NestedKeyOfArray<T extends object> = ReadonlyArray<NestedKeyOf<T>>

export const getNestedValue = <O extends object, K extends NestedKeyOf<O>>(
  val: O,
  path: K
): GetNestedType<O, K> | undefined => {
  const pathSegments = path.split('.') as PathSegments<K>

  const traverse = <T extends object>(
    obj: T,
    segments: readonly string[]
  ): GetNestedType<O, K> | undefined => {
    if (segments.length === 0) {
      return obj as GetNestedType<O, K>
    }

    const [currentKey, ...remainingSegments] = segments

    if (!(currentKey in obj)) {
      return undefined
    }

    const currentValue = obj[currentKey as keyof T]

    // Handle undefined/null values (from optional properties)
    if (currentValue === undefined || currentValue === null) {
      return undefined
    }

    if (remainingSegments.length === 0) {
      return currentValue as GetNestedType<O, K>
    }

    if (typeof currentValue === 'object') {
      return traverse(currentValue as object, remainingSegments)
    }

    return undefined
  }

  return traverse(val, pathSegments)
}

export const chunkArr = <T>(arr: T[], size: number): T[][] => {
  const result: T[][] = []
  while (arr.length) {
    result.push(arr.splice(0, size))
  }
  return result
}

export const getStandardGetRowIdFn =
  <TData extends { id: string | number } | undefined>() =>
  ({ data }: GetRowIdParams<TData>) => {
    return `${data?.id ?? ''}`
  }
